import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import useLocalization from '../../../hooks/useLocalization';

const styles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 1,
  '& .MuiToggleButtonGroup-grouped': {
    m: 0,
    minHeight: 44,
    px: 1.5,
    py: 1,
    border: 0,
    borderRadius: 2,
    bgcolor: 'neutral.600',
    color: 'neutral.100',
    typography: 'body2',
    fontWeight: 600,
    textTransform: 'none',
    transition: 'background-color 150ms ease, color 150ms ease, transform 120ms ease-out',
    '&:hover': { bgcolor: 'neutral.500' },
    '&:active:not(.Mui-disabled)': { transform: 'scale(0.98)' },
    '&.Mui-focusVisible': {
      outline: '2px solid',
      outlineColor: 'primary.main',
      outlineOffset: 2,
    },
    '&.Mui-selected': {
      bgcolor: 'primary.dark',
      color: 'primary.contrastText',
      '&:hover': { bgcolor: 'primary.dark' },
    },
    '&.Mui-disabled': { color: 'text.disabled' },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&:active:not(.Mui-disabled)': { transform: 'none' },
    },
  },
};

type FinpspPaymentTypeFieldProps<T extends FieldValues, TType extends string, TTransformedValues = T> = {
  name: Path<T>;
  control: Control<T, unknown, TTransformedValues>;
  types: TType[];
  value: TType;
  channels: Record<TType, { actionKey: string | null }>;
  disabled: boolean;
};

const FinpspPaymentTypeField = <T extends FieldValues, TType extends string, TTransformedValues = T>({
  name,
  control,
  types,
  value,
  channels,
  disabled,
}: FinpspPaymentTypeFieldProps<T, TType, TTransformedValues>) => {
  const { t } = useLocalization();

  if (types.length < 2) {
    return null;
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <ToggleButtonGroup
          exclusive
          value={value}
          onChange={(_event, nextValue: TType | null) => {
            if (nextValue !== null && types.includes(nextValue)) {
              field.onChange(nextValue);
            }
          }}
          aria-label={t('finpspPaymentType')}
          disabled={disabled}
          sx={styles}
        >
          {types.map((type) => {
            const { actionKey } = channels[type];

            return (
              <ToggleButton key={type} value={type}>
                {type}
                {actionKey && ` · ${t(actionKey)}`}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      )}
    />
  );
};

export default FinpspPaymentTypeField;
